import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, FileText, Linkedin, Briefcase, Users, FolderOpen, Shirt, GraduationCap, Search, UserCheck, Target, Eye, DollarSign, Award, MessageSquare, FileEdit, PenTool, Share2, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import PageBanner from '@/components/shared/PageBanner';
import CTABanner from '@/components/shared/CTABanner';
import { DCA_LINKS } from '@/lib/dcaConfig';
import { workshopTopicsData } from '@/data/workshopTopicsData';
import { publicApi } from '@/lib/api';

const iconMap = {
  FileText, Linkedin, Briefcase, Users, FolderOpen, Shirt, GraduationCap, 
  Search, UserCheck, Target, Eye, DollarSign, Award, MessageSquare, 
  FileEdit, PenTool, Share2, Edit3
};

const WorkshopTopicPage = () => {
  const { slug } = useParams();
  const [settings, setSettings] = useState(null);
  const topic = workshopTopicsData[slug];

  useEffect(() => {
    publicApi.getSiteSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  if (!topic) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-4xl font-heading font-bold mb-4">Topic Not Found</h1>
            <p className="text-muted-foreground mb-8">The workshop topic you're looking for doesn't exist.</p>
            <Button asChild>
              <Link to="/service/workshop">Back to Workshops</Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[topic.icon] || FileText;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <PageBanner 
          title={topic.title}
          breadcrumbs={[
            { label: 'Services', path: '/services' },
            { label: 'Community Workshops', path: '/service/workshop' },
            { label: topic.title }
          ]} 
        />

        {/* Topic Content */}
        <section className="py-16" data-testid="workshop-topic-detail">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link 
              to="/service/workshop" 
              className="inline-flex items-center text-primary hover:text-primary/80 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Topics
            </Link>

            {/* Icon and Title */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                <IconComponent className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold">{topic.title}</h1>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-8 mb-12">
              <h2 className="text-xl font-heading font-bold mb-4 text-primary">Overview</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {topic.summary}
              </p>
            </div>

            {/* Importance */}
            <div className="mb-12">
              <h2 className="text-2xl font-heading font-bold mb-4">Why This Matters</h2>
              <p className="text-muted-foreground leading-relaxed">
                {topic.importance}
              </p>
            </div>

            {/* Key Points */}
            <div className="mb-12">
              <h2 className="text-2xl font-heading font-bold mb-6">Key Insights & Strategies</h2>
              <div className="space-y-6">
                {topic.keyPoints.map((point, index) => (
                  <div 
                    key={index}
                    className="bg-white border border-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg mb-2">{point.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{point.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-slate-900 text-white rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-heading font-bold mb-6">Quick Takeaways</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {topic.keyPoints.slice(0, 4).map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm">{point.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 text-center text-white">
              <h2 className="text-2xl font-heading font-bold mb-4">
                Want to Learn More?
              </h2>
              <p className="text-white/90 mb-6 max-w-xl mx-auto">
                {topic.callToAction}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-accent hover:bg-accent/90"
                >
                  <Link to="/contact-us">
                    Request This Workshop <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/10"
                  data-testid="schedule-consultation-workshop-btn"
                  onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
                >
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>

      <Footer />
    </div>
  );
};

export default WorkshopTopicPage;
